import React, { useContext, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Toaster, toast } from 'sonner';

import { ROLES } from '../../App';
import { AuthenticationContext } from '../../context/Authentication';
import useAuth from '../../hooks/useAuth';
import { getRedirectPath } from '../../util/AuthRedirect';

import { Input } from '../businessSignUp/components/Input';
import { Label } from '../businessSignUp/components/Label';
import Button from '../../components/Button';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

const businessLoginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Valid email is required'),
  password: z.string().min(1, 'Password is required'),
});

const Businesslogin = () => {
  const authentication = useContext(AuthenticationContext);
  const { setAuth } = useAuth();
  const server_url = process.env.REACT_APP_API_URI;
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(businessLoginSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleLoginFormSubmit = async (dataForm) => {
    try {
      const response = await axios.post(
        `${server_url}/business/login`,
        JSON.stringify(dataForm),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.data.isLoggedIn === true) {
        const data = jwtDecode(response.data.token);
        const { token } = response.data;
        const roles = data.role;
        const { email } = data;
        setAuth({ email, roles, token });

        authentication.login();
        localStorage.setItem('loginToken', response.data.token);

        toast.success('Business Login successful!');
        navigate(
          getRedirectPath({
            location,
            roles: data.role,
            adminRole: ROLES.Admin,
          }),
        );
      } else {
        toast.error(response.data.msg || 'Invalid credentials');
      }
    } catch (err) {
      console.log(err);
      toast.error('An error occurred during login.');
    }
  };

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)] py-12 bg-primary font-montserrat px-4">
        <div className="w-full max-w-md bg-[#111827] shadow-2xl rounded-3xl overflow-hidden border border-slate-800">
          <div className="bg-gradient-to-br from-[#081325] to-[#112140] p-8 md:p-10 text-white relative flex flex-col items-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="absolute left-6 top-6 flex items-center text-sm gap-1 text-white/70 hover:text-white transition-colors"
            >
              <ArrowBackIosNewIcon fontSize="small" />
              Back
            </button>
            <h1 className="text-3xl md:text-4xl font-bold text-center mt-6">
              Welcome.
            </h1>
            <p className="mt-4 text-blue-200 text-center max-w-xs text-sm">
              Business Login
            </p>
          </div>

          <div className="p-8 md:p-10">
            <form
              onSubmit={handleSubmit(handleLoginFormSubmit)}
              className="space-y-6 animate-in slide-in-from-right-4 duration-500"
            >
              <div className="space-y-2">
                <Label htmlFor="email">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email"
                  {...register('email')}
                />
                {errors.email && (
                  <span className="text-xs text-red-500 font-medium">
                    {errors.email.message}
                  </span>
                )}
              </div>

              <div className="space-y-2 relative">
                <Label htmlFor="password">
                  Password <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    {...register('password')}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-300"
                  >
                    {showPassword ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <EyeOff className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <span className="text-xs text-red-500 font-medium">
                    {errors.password.message}
                  </span>
                )}
              </div>

              <div className="text-center pt-2">
                <p className="text-slate-400 text-xs mb-4">
                  We'll never share your email with anyone else.
                </p>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-all font-semibold py-3 shadow-lg shadow-blue-500/30"
                >
                  {isSubmitting ? 'Logging in...' : 'Login'}{' '}
                  <LogIn className="w-5 h-5 ml-1" />
                </Button>
              </div>
            </form>

            <div className="mt-8 space-y-3 text-sm text-center">
              <div className="text-slate-400">
                Normal account?{' '}
                <Link
                  to="/login"
                  className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                >
                  Login here.
                </Link>
              </div>
              <div className="text-slate-400">
                No account?{' '}
                <Link
                  to="/register"
                  className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                >
                  Let's Register.
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Businesslogin;
